import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { POST_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { BulkActionResponseData } from '@utils/types/back-office/priority-queue';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type BulkCleanupDeadLetterParams = {
  max_items?: number;
  older_than_hours?: number;
};

export const useBulkCleanupDeadLetter = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const bulkCleanupDeadLetter = async (
    params?: BulkCleanupDeadLetterParams
  ) => {
    const payload = {
      max_items: params?.max_items ?? 100,
      older_than_hours: params?.older_than_hours ?? 168
    };
    const response = await Axios.post<BulkActionResponseData>(
      `${POST_URL().bulkCleanupDeadLetter}`,
      payload
    );
    return response.data;
  };

  return useMutation<
    BulkActionResponseData,
    AxiosError<BaseError>,
    BulkCleanupDeadLetterParams | undefined
  >(bulkCleanupDeadLetter, {
    onSuccess: data => {
      const count = data.result?.affected_count || 0;
      toast.success(`${t('cleaned-up-dead-letter-items')}: ${count}`);
      queryClient.invalidateQueries(['priorityQueue']);
      queryClient.invalidateQueries(['queueSummary']);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-cleanup-dead-letter-items');
      toast.error(errorMessage);
    }
  });
};
